import immutableStore from 'alt-utils/lib/ImmutableUtil';
import immutable from 'immutable';

import alt from 'components/Dispatcher';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import CrmSyncAccountAndOpportunitiesSource from 'sources/crm/CrmSyncAccountAndOpportunitiesSource';

/**
 * Store the state of <CallSyncCRMRelatedToSearchInputBox/> when it's looking for account-and-opportunities based on
 * a given contact-account-id from <CallSyncCRMContactLeadSearchInputBox/>
 */
@immutableStore
class CrmSyncAccountAndOpportunitiesStore {
  constructor() {
    this.state = immutable.Map({
      // options store the list of possible accounts/opportunities for the current contact-account-id
      options: immutable.List(),
      // values store the list of current values chosen by the user as the account/opportunity for the current
      // contact with an account-id
      values: immutable.List(),
      // populateAccount is a boolean indicating whether this.state.get('values') should be auto-populated with an
      // an account or not for a given contact that's chosen by the user
      populateAccount: null,
    });
    this.bindActions(CrmSyncActions);
    this.registerAsync(CrmSyncAccountAndOpportunitiesSource);
  }

  /**
   * Update this.state.get('values') with `values`
   * @param {Array.<Object>} values Array of values chosen by the user for the related-to field
   */
  onUpdateAccountsAndOpportunitiesValues(values) {
    const state = this.state.set('values', immutable.List(values));
    this.setState(state);
  }

  /** Reset this.state.get('values') back to immutable.List() */
  onClearAccountsAndOpportunitiesValues() {
    const state = this.state.set('values', immutable.List());
    this.setState(state);
  }

  /** Reset this.state.get('options') back to immutable.List() */
  onClearAccountsAndOpportunitiesOptions() {
    const state = this.state.set('options', immutable.List());
    this.setState(state);
  }

  /**
   * Callback triggered when the user wants to find accounts/opportunities for a given contact-account-id
   * @param {String} contactName Contact Account Id value
   */
  onFindAccountsAndOpportunitiesByContactName(contactName) {
    setImmediate(() => {
      this.getInstance().findAccountsAndOpportunitiesByContactName(contactName);
    });
  }

  /**
   * Populate this.state.get('options') with a server-side response that includes the account/opportunities for a given
   * contact-account-id
   * @param {Object} resp Server-side response that includes account/opportunities related data
   */
  onFindAccountsAndOpportunitiesByContactNameSuccess(resp) {
    const data = resp.data;
    let state = this.state.set('options', immutable.List(data.accounts_and_opportunities));
    let contact = state.get('populateAccount');
    if (contact !== null) {
      const options = state.get('options');
      const option = options
        .filter(opt => {
          return opt.id === contact.account.id;
        })
        .first();
      if (option) {
        state = state.set('values', immutable.List([this.accountItemFromOption(option)]));
      }
      state = state.set('populateAccount', null);
    }
    this.setState(state);
  }

  /**
   * Return an item that's ready for render in a react-selectize component for a given option
   * @param {Object} option Option containing the fields for a CRM account
   */
  accountItemFromOption(option) {
    return {
      id: option.id,
      label: option.name,
      value: option.name,
      option: option,
    };
  }

  /**
   * Set this.state.get('populateAccount') to true so that when the account / opportunities are returned by the server,
   * we auto-populate this.state.get('values') with the first account found for this contact
   * @param {object} contact CRM contact that contains its account info as well
   */
  onPopulateAccountForContact(contact) {
    if (contact && contact.account) {
      const state = this.state.set('populateAccount', contact);
      this.setState(state);
      this.onFindAccountsAndOpportunitiesByContactName(contact.name);
    }
  }
}

export default alt.createStore(
  CrmSyncAccountAndOpportunitiesStore,
  'CrmSyncAccountAndOpportunitiesStore'
);
